import { useMutation } from '@apollo/client';

import { useEventCallback } from '../../../common/utils/hooks';
import {
	MODIFY_PICK_LIST_ITEM,
	PICK_LIST_ITEM_TOGGLE_IS_PICKED,
	PICK_LIST_ITEM_SET_IS_PICKED_BULK,
	MODIFY_PICKLIST_ITEMS_QUANTITY,
} from '../../../common/graphql/pickListItem';

import { getPickedQuantity, normalizeExpiryDatesForSave, toNonNegativeNumber } from './PickListDetailHelpers';

// Pick list item changes (tick, batch tick, quantity, expiry dates at pick)
// that keep the screen's mapped item list in step with the server response.
const usePickListItemActions = ({
	pickList,
	mappedPickInventory,
	setMappedPickInventory,
	canEditPickList,
	setLastPickedProductAction,
	setRefreshItems,
}) => {
	const [toggleIsPicked, { loading: loadingToggleIsPicked }] = useMutation(PICK_LIST_ITEM_TOGGLE_IS_PICKED);
	const [modifyPickListItemMutation] = useMutation(MODIFY_PICK_LIST_ITEM);
	const [setPickListItemIsPickedBulkMutation, { loading: loadingSetPickedBulk }] = useMutation(PICK_LIST_ITEM_SET_IS_PICKED_BULK);
	const [modifyPicklistItemsQuantityMutation, { loading: loadingModifyPickListItemsQuantity }] = useMutation(MODIFY_PICKLIST_ITEMS_QUANTITY);

	const pickListItemToggleIsPicked = useEventCallback(async id => {
		if (!canEditPickList) {
			return null;
		}

		const previousItem = mappedPickInventory.find(item => item.id === id);
		const previousPickedQuantity = getPickedQuantity(previousItem);
		const toggleIsPickedResponse = await toggleIsPicked({
			variables: {
				id,
				isPicked: !(previousItem?.isPicked && previousItem?.extraQuantityIsPicked),
			},
		});

		const {
			extraQuantityIsPicked,
			isPicked,
			quantity,
			extraQuantity,
			tubWeightExcludedQuantity,
			planogramItemExpiryDatesAtPick,
		} = toggleIsPickedResponse.data.toggleIsPicked;

		// needed when we change settings 'batch items'
		mappedPickInventory.forEach((item) => {
			if (item.id === toggleIsPickedResponse.data.toggleIsPicked.id) {
				item.pickedQuantity = isPicked ? quantity : 0;
				item.pickedExtraQuantity = extraQuantityIsPicked ? extraQuantity : 0;
				item.isPicked = isPicked;
				item.extraQuantityIsPicked = extraQuantityIsPicked;
				item.tubWeightExcludedQuantity = toNonNegativeNumber(tubWeightExcludedQuantity);
				item.planogramItemExpiryDatesAtPick = planogramItemExpiryDatesAtPick || null;
				item.expiryDates = planogramItemExpiryDatesAtPick || null;
			}
			return { ...item };
		});
		// Force a new reference so parent memoized computations (e.g. tub weight) update.
		setMappedPickInventory([...mappedPickInventory]);

		const nextPickedQuantity = (isPicked ? toNonNegativeNumber(quantity) : 0) +
			(extraQuantityIsPicked ? toNonNegativeNumber(extraQuantity) : 0);
		const pickedQuantityIncrease = Math.max(0, nextPickedQuantity - previousPickedQuantity);
		if (pickedQuantityIncrease > 0 && previousItem?.product?.id) {
			setLastPickedProductAction({
				productId: previousItem.product.id,
				productName: previousItem.product.name,
				quantity: pickedQuantityIncrease,
			});
		}

		const updatedItem = {
			...toggleIsPickedResponse.data.toggleIsPicked,
			pickedQuantity: isPicked ? quantity : 0,
			pickedExtraQuantity: extraQuantityIsPicked ? extraQuantity : 0,
			expiryDates: planogramItemExpiryDatesAtPick || null,
		};

		return updatedItem;
	});

	const setPickListItemIsPickedBulk = useEventCallback(async (productId, isPicked) => {
		if (!canEditPickList) {
			return null;
		}

		const previousPickedQuantityForProduct = mappedPickInventory.reduce((acc, item) => {
			if (item?.product?.id !== productId) {
				return acc;
			}
			return acc + getPickedQuantity(item);
		}, 0);

		const isPickedBulkResponse = await setPickListItemIsPickedBulkMutation({
			variables: {
				productId,
				isPicked,
				serviceRunId: pickList.serviceRun.id,
				machineId: pickList.machine.id,
			},
		});
		// needed when we change settings 'batch items'
		const updatedProducts = isPickedBulkResponse.data.setIsPickedBulk;
		const batchedProduct = { id: [], pickedQuantity: 0, pickedExtraQuantity: 0, isPicked: false, extraQuantityIsPicked: false };
		mappedPickInventory.forEach((item) => {
			const updatedProduct = updatedProducts.find(updatedItem => updatedItem.id === item.id);

			if (updatedProduct) {

				const { extraQuantityIsPicked, isPicked } = updatedProduct;
				item.pickedQuantity = isPicked ? updatedProduct.quantity : 0;
				item.pickedExtraQuantity = extraQuantityIsPicked ? updatedProduct.extraQuantity : 0;
				item.isPicked = isPicked;
				item.extraQuantityIsPicked = extraQuantityIsPicked;
				item.tubWeightExcludedQuantity = toNonNegativeNumber(updatedProduct.tubWeightExcludedQuantity);
				item.planogramItemExpiryDatesAtPick = updatedProduct.planogramItemExpiryDatesAtPick || null;
				item.expiryDates = updatedProduct.planogramItemExpiryDatesAtPick || null;

				// pass to PlanogramItem
				batchedProduct.id.push(updatedProduct.id);
				batchedProduct.pickedQuantity += isPicked ? updatedProduct.quantity : 0;
				batchedProduct.pickedExtraQuantity += extraQuantityIsPicked ? updatedProduct.extraQuantity || 0 : 0;
				batchedProduct.isPicked = isPicked;
				batchedProduct.extraQuantityIsPicked = extraQuantityIsPicked;
			}
			return { ...item };
		});
		// Force a new reference so parent memoized computations (e.g. tub weight) update.
		setMappedPickInventory([...mappedPickInventory]);

		const nextPickedQuantityForProduct = mappedPickInventory.reduce((acc, item) => {
			if (item?.product?.id !== productId) {
				return acc;
			}
			return acc + getPickedQuantity(item);
		}, 0);
		const pickedQuantityIncrease = Math.max(0, nextPickedQuantityForProduct - previousPickedQuantityForProduct);
		if (pickedQuantityIncrease > 0) {
			const productName = mappedPickInventory.find(item => item?.product?.id === productId)?.product?.name || 'Product';
			setLastPickedProductAction({
				productId,
				productName,
				quantity: pickedQuantityIncrease,
			});
		}

		return batchedProduct;
	});

	const setPickListItemQuantity = useEventCallback(async (ids, quantity, includePickedQuantity = false) => {
		if (!canEditPickList) {
			return;
		}

		const updatedItems = await modifyPicklistItemsQuantityMutation({
			variables: { ids, quantity, includePickedQuantity },
		});
		setMappedPickInventory(mappedPickInventory.map((mappedItem) => {
			const updatedItem = updatedItems.data.modifyPicklistItemsQuantity.find(updatedItem => mappedItem.id === updatedItem.id);
			if (updatedItem) {
				mappedItem.extraQuantity = updatedItem.extraQuantity;
				mappedItem.requiredQuantity = updatedItem.quantity + updatedItem.extraQuantity;
				mappedItem.isPicked = updatedItem.isPicked;
				mappedItem.extraQuantityIsPicked = updatedItem.extraQuantityIsPicked;
				mappedItem.pickedQuantity = updatedItem.isPicked ? updatedItem.quantity : 0;
				mappedItem.pickedExtraQuantity = updatedItem.extraQuantityIsPicked ? updatedItem.extraQuantity : 0;
				mappedItem.tubWeightExcludedQuantity = toNonNegativeNumber(updatedItem.tubWeightExcludedQuantity);
				mappedItem.expiryDates = updatedItem.planogramItemExpiryDatesAtPick;
				mappedItem.planogramItemExpiryDatesAtPick = updatedItem.planogramItemExpiryDatesAtPick;
				return { ...mappedItem };
			}
			return mappedItem;
		}));
	});

	const setPickListItemExpiryDates = useEventCallback(async ({
		pickListItemIds,
		expiryDates,
	}) => {
		if (!expiryDates || !canEditPickList) {
			return;
		}

		const idsToUpdate = new Set((pickListItemIds || []).map(id => id.toString()));
		const pickedIdsToUpdate = mappedPickInventory
			.filter(item => idsToUpdate.has(item.id.toString()) && getPickedQuantity(item) > 0)
			.map(item => item.id);
		const pickedIdsToUpdateSet = new Set(pickedIdsToUpdate.map(id => id.toString()));
		const normalizedExpiryDates = normalizeExpiryDatesForSave(expiryDates);

		if (!pickedIdsToUpdate.length) {
			return;
		}

		await Promise.all(pickedIdsToUpdate.map(id => modifyPickListItemMutation({
			variables: {
				input: {
					id,
					planogramItemExpiryDatesAtPick: normalizedExpiryDates,
				},
			},
		})));

		setMappedPickInventory(prevItems => prevItems.map((item) => {
			if (!pickedIdsToUpdateSet.has(item.id.toString())) {
				return item;
			}

			return {
				...item,
				expiryDates: normalizedExpiryDates,
				planogramItemExpiryDatesAtPick: normalizedExpiryDates,
			};
		}));
	});

	// Handler for adding quantity sold after recalculation (called from the +X button)
	const handleAddMissingQuantity = useEventCallback(async (id, requiredQuantity) => {
		const ids = Array.isArray(id) ? id : [id];
		await setPickListItemQuantity(ids, requiredQuantity, true);
		// Refresh the items to update the UI
		setRefreshItems(true);
	});

	return {
		pickListItemToggleIsPicked,
		setPickListItemIsPickedBulk,
		setPickListItemQuantity,
		setPickListItemExpiryDates,
		handleAddMissingQuantity,
		loadingToggleIsPicked,
		loadingSetPickedBulk,
		loadingModifyPickListItemsQuantity,
	};
};

export default usePickListItemActions;
